import React from "react";
import {
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
} from "@mui/material";
import { SingleChoiceAnswer, SingleChoiceQuestion } from "../../types";

interface SingleChoiceQuestionProps {
  question: SingleChoiceQuestion;
  value: SingleChoiceAnswer | undefined;
  onChange: (answer: SingleChoiceAnswer) => void;
}

export const SingleChoiceQuestionField: React.FC<SingleChoiceQuestionProps> = ({
  question,
  value,
  onChange,
}) => {
  const selectedValue = value?.choice ?? "";
  const isCustomSelected =
    question.customOption && selectedValue === question.customOption.value;

  const handleSelect = (choice: string) => {
    onChange({
      choice,
      customText:
        question.customOption && choice === question.customOption.value
          ? value?.customText ?? ""
          : undefined,
    });
  };

  const handleCustomChange = (text: string) => {
    if (!question.customOption) {
      return;
    }
    onChange({
      choice: question.customOption.value,
      customText: text,
    });
  };

  return (
    <FormControl component="fieldset" fullWidth>
      <RadioGroup
        value={selectedValue}
        onChange={(event) => handleSelect(event.target.value)}
      >
        <Stack spacing={1}>
          {question.options.map((option) => {
            const isSelected = option.value === selectedValue;
            return (
              <Card
                key={option.value}
                variant="outlined"
                sx={{
                  cursor: "pointer",
                  borderColor: isSelected ? "primary.main" : undefined,
                  borderWidth: isSelected ? 2 : 1,
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "action.hover",
                  },
                }}
                onClick={() => handleSelect(option.value)}
              >
                <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                  <FormControlLabel
                    value={option.value}
                    control={<Radio checked={isSelected} />}
                    label={
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        {option.icon && (
                          <Box
                            sx={{
                              mr: 1,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {option.icon}
                          </Box>
                        )}
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle1">
                            {option.label}
                          </Typography>
                          {option.description && (
                            <Typography variant="body2" color="text.secondary">
                              {option.description}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    }
                    sx={{ m: 0, width: "100%" }}
                  />
                </CardContent>
              </Card>
            );
          })}
          {question.customOption && (
            <Card
              key={question.customOption.value}
              variant="outlined"
              sx={{
                cursor: "pointer",
                borderColor: isCustomSelected ? "primary.main" : undefined,
                borderWidth: isCustomSelected ? 2 : 1,
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
              onClick={() => handleSelect(question.customOption!.value)}
            >
              <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                <Stack spacing={1.5}>
                  <FormControlLabel
                    value={question.customOption.value}
                    control={<Radio checked={isCustomSelected} />}
                    label={
                      <Typography variant="subtitle1">
                        {question.customOption.label}
                      </Typography>
                    }
                    sx={{ m: 0, width: "100%" }}
                  />
                  {isCustomSelected && (
                    <TextField
                      fullWidth
                      value={value?.customText ?? ""}
                      onChange={(event) =>
                        handleCustomChange(event.target.value)
                      }
                      placeholder={question.customOption.placeholder}
                      helperText={question.customOption.helperText}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </RadioGroup>
    </FormControl>
  );
};
